import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { Building, getBuildings } from 'services/api/buildings';
import { toast } from 'sonner';

// Global cache reference to avoid duplicate API calls
// This will be populated by the useHomePageData hook
declare global {
  var __buildingsGlobalCache: {
    data: Building[] | null;
    timestamp: number;
  } | undefined;
}

interface BuildingsContextType {
  buildings: Building[];
  loading: boolean;
  error: string | null;
  refreshBuildings: () => Promise<void>;
  // Pagination
  currentPage: number;
  pageSize: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  // Sorting
  sortField: keyof Building | null;
  sortDirection: 'asc' | 'desc';
  setSortField: (field: keyof Building | null) => void;
  setSortDirection: (direction: 'asc' | 'desc') => void;
  // Filtering
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  // Computed values
  filteredBuildings: Building[];
  paginatedBuildings: Building[];
  totalFilteredCount: number;
}

const BuildingsContext = createContext<BuildingsContextType>({
  buildings: [],
  loading: false,
  error: null,
  refreshBuildings: async () => {},
  currentPage: 1,
  pageSize: 10,
  totalPages: 1,
  setCurrentPage: () => {},
  setPageSize: () => {},
  sortField: null,
  sortDirection: 'asc',
  setSortField: () => {},
  setSortDirection: () => {},
  searchTerm: '',
  setSearchTerm: () => {},
  filteredBuildings: [],
  paginatedBuildings: [],
  totalFilteredCount: 0,
});

export const useBuildings = () => useContext(BuildingsContext);

export const BuildingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // Sorting state
  const [sortField, setSortField] = useState<keyof Building | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Filtering state
  const [searchTerm, setSearchTerm] = useState('');

  const fetchBuildings = useCallback(async (forceRefreshToken: boolean = false) => {
    if (!user) {
      setBuildings([]);
      setError(null);
      return;
    }

    // Check if we have fresh data in global cache (within 30 seconds)
    const now = Date.now();
    const cacheAge = 30000; // 30 seconds
    
    if (!forceRefreshToken && 
        global.__buildingsGlobalCache && 
        global.__buildingsGlobalCache.data && 
        (now - global.__buildingsGlobalCache.timestamp) < cacheAge) {
      console.log('BuildingsContext: Using cached buildings data');
      setBuildings(global.__buildingsGlobalCache.data);
      setError(null);
      setCurrentPage(1);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const buildingsData = await getBuildings(forceRefreshToken);
      setBuildings(buildingsData);
      
      // Update global cache
      global.__buildingsGlobalCache = {
        data: buildingsData,
        timestamp: Date.now()
      };
      
      // Reset to first page when new data is loaded
      setCurrentPage(1);
    } catch (err: any) {
      console.error('Failed to fetch buildings:', err);
      setError(err.message);
      
      // Show appropriate error messages based on error type
      if (err.message.includes('Insufficient role/permissions')) {
        toast.error('Non hai le autorizzazioni necessarie per vedere gli stabili.', {className: 'bg-red-100 text-red-800 border-red-300'});
      } else if (err.message.includes('Invalid or missing token')) {
        toast.error('Authentication error. Effetua nuovamente l\'accesso.', {className: 'bg-red-100 text-red-800 border-red-300'});
      } else if (err.message.includes('Rate limited')) {
        toast.error('Troppe richieste. Riprova più tardi.', {className: 'bg-red-100 text-red-800 border-red-300'});
      } else if (err.message.includes('Server error')) {
        toast.error('Server error. Riprova più tardi.', {className: 'bg-red-100 text-red-800 border-red-300'});
      } else {
        toast.error('Errore caricamento stabili.', {className: 'bg-red-100 text-red-800 border-red-300'});
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  const refreshBuildings = async () => {
    await fetchBuildings(true); // Force refresh token
  };

  // Computed values using useMemo for performance
  const filteredBuildings = useMemo(() => {
    if (!searchTerm.trim()) return buildings;
    
    const term = searchTerm.toLowerCase();
    return buildings.filter(building => 
      (building.displayName || building.name || '').toLowerCase().includes(term) ||
      (building.address || '').toLowerCase().includes(term) ||
      building.id.toLowerCase().includes(term)
    );
  }, [buildings, searchTerm]);

  const sortedBuildings = useMemo(() => {
    if (!sortField) return filteredBuildings;
    
    return [...filteredBuildings].sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      
      // Handle null/undefined values
      if (!aValue && !bValue) return 0;
      if (!aValue) return sortDirection === 'asc' ? -1 : 1;
      if (!bValue) return sortDirection === 'asc' ? 1 : -1;
      
      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Handle Firestore timestamps
      if (typeof aValue === 'object' && typeof bValue === 'object') {
        const aTime = (aValue as any)._seconds ? Number((aValue as any)._seconds) : 0;
        const bTime = (bValue as any)._seconds ? Number((bValue as any)._seconds) : 0;
        return sortDirection === 'asc' ? aTime - bTime : bTime - aTime;
      }
      
      // Default string comparison
      const aStr = String(aValue);
      const bStr = String(bValue);
      return sortDirection === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  }, [filteredBuildings, sortField, sortDirection]);

  const totalFilteredCount = sortedBuildings.length;
  const totalPages = Math.ceil(totalFilteredCount / pageSize);
  
  const paginatedBuildings = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedBuildings.slice(startIndex, startIndex + pageSize);
  }, [sortedBuildings, currentPage, pageSize]);

  // Automatically fetch buildings when user logs in
  useEffect(() => {
    if (user) {
      // Use fresh token on first fetch after login
      fetchBuildings(true);
    } else {
      // Clear buildings when user logs out
      setBuildings([]);
      setError(null);
      setCurrentPage(1);
      setSearchTerm('');
      setSortField(null);
    }
  }, [user, fetchBuildings]);

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortField, sortDirection]);

  return (
    <BuildingsContext.Provider value={{ 
      buildings, 
      loading, 
      error, 
      refreshBuildings,
      currentPage,
      pageSize,
      totalPages,
      setCurrentPage,
      setPageSize,
      sortField,
      sortDirection,
      setSortField,
      setSortDirection,
      searchTerm,
      setSearchTerm,
      filteredBuildings,
      paginatedBuildings,
      totalFilteredCount
    }}>
      {children}
    </BuildingsContext.Provider>
  );
};
